import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const RESEND_COOLDOWN = 120; // 2 minutes
const SHOW_TIMER_AFTER = 20; // show countdown after 20 seconds
const MAX_RESENDS = 3;

export default function EmailPendingPage() {
  const { user, token, api, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [secondsLeft, setSecondsLeft] = useState(SHOW_TIMER_AFTER);
  const [showTimer, setShowTimer] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [exhausted, setExhausted] = useState(false);
  const [initialSent, setInitialSent] = useState(true); // first email sent on register

  // Start the initial 20-second wait then show the full cooldown timer
  useEffect(() => {
    if (exhausted) return;
    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!showTimer) {
            // After 20s initial wait, show the countdown for remaining cooldown
            setShowTimer(true);
            return RESEND_COOLDOWN - SHOW_TIMER_AFTER;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [showTimer, exhausted, resendCount]);

  const canResend = showTimer && secondsLeft === 0 && resendCount < MAX_RESENDS && !exhausted;

  const handleResend = useCallback(async () => {
    if (!canResend || sending) return;
    setSending(true);
    setMessage('');
    try {
      await api().post('/auth/resend-verification');
      const newCount = resendCount + 1;
      setResendCount(newCount);
      setMessage('Verification email sent! Check your inbox.');
      if (newCount >= MAX_RESENDS) {
        setExhausted(true);
      } else {
        // Reset the cooldown
        setShowTimer(false);
        setSecondsLeft(SHOW_TIMER_AFTER);
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to resend. Please try again.';
      setMessage(errMsg);
    } finally {
      setSending(false);
    }
  }, [canResend, sending, resendCount, api]);

  // Check if already verified (poll on focus / interval)
  useEffect(() => {
    const check = async () => {
      try {
        if (refreshUser) {
          const verified = await refreshUser();
          if (verified) {
            navigate('/lung-cancer', { replace: true });
          }
        }
      } catch {}
    };
    const interval = setInterval(check, 15000);
    window.addEventListener('focus', check);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', check);
    };
  }, [refreshUser, navigate]);

  if (!token) {
    navigate('/login', { replace: true });
    return null;
  }

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="d-flex align-items-center" style={{ minHeight: '100vh', background: '#f5f7fb' }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-sm-10 col-md-8 col-lg-5">
            <div className="text-center mb-4">
              <img
                src={process.env.PUBLIC_URL + '/img/cancepro-logo.png'}
                alt="CancePro"
                width="60"
                height="60"
                className="mb-2"
              />
              <h2 className="fw-bold">CancePro MedX</h2>
            </div>

            <div className="card">
              <div className="card-body p-4 text-center">
                {/* Email icon */}
                <div style={{ fontSize: '56px', marginBottom: '12px' }}>&#9993;</div>

                <h4 className="fw-bold mb-3" style={{ color: '#dc8a00' }}>
                  Email Verification Pending
                </h4>

                <p className="text-muted mb-2" style={{ lineHeight: 1.7 }}>
                  {initialSent
                    ? 'A verification email has been sent to:'
                    : 'Please verify your email address:'}
                </p>

                <p className="fw-semibold mb-3" style={{ color: '#0d6efd' }}>
                  {user?.email || 'your email'}
                </p>

                <div
                  className="alert alert-warning mb-3"
                  style={{ fontSize: '14px', lineHeight: 1.6 }}
                >
                  Email verification is pending. Please check your inbox after 2 minutes
                  for the verification email. Check your spam/junk folder as well.
                </div>

                {/* Status message */}
                {message && (
                  <div
                    className={`alert ${message.includes('sent') ? 'alert-success' : 'alert-danger'} py-2`}
                    style={{ fontSize: '13px' }}
                  >
                    {message}
                  </div>
                )}

                {/* Exhausted state */}
                {exhausted && (
                  <div className="alert alert-info py-2" style={{ fontSize: '13px' }}>
                    You have used all {MAX_RESENDS} resend attempts. Please close your browser
                    and come back to try again, or check your email inbox (including spam folder).
                  </div>
                )}

                {/* Timer / Resend */}
                {!exhausted && (
                  <div className="mt-3">
                    {!showTimer && (
                      <p className="text-muted" style={{ fontSize: '13px' }}>
                        Please wait...
                      </p>
                    )}

                    {showTimer && secondsLeft > 0 && (
                      <p className="text-muted" style={{ fontSize: '14px' }}>
                        Resend available in{' '}
                        <span className="fw-bold" style={{ color: '#0d6efd', fontSize: '16px' }}>
                          {formatTime(secondsLeft)}
                        </span>
                      </p>
                    )}

                    {canResend && (
                      <button
                        className="btn btn-outline-primary"
                        onClick={handleResend}
                        disabled={sending}
                      >
                        {sending ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                            Sending...
                          </>
                        ) : (
                          `Resend Verification Email (${MAX_RESENDS - resendCount} left)`
                        )}
                      </button>
                    )}
                  </div>
                )}

                <hr className="my-3" />

                <button
                  className="btn btn-link text-muted"
                  style={{ fontSize: '13px' }}
                  onClick={() => {
                    logout();
                    navigate('/login', { replace: true });
                  }}
                >
                  Sign in with a different account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
